// All tunable knobs for a run live here. A run is fully determined by
// (seed, SimParams), so this object is exactly what gets saved to the server as
// an "experiment". Keep it a plain, JSON-serializable structure.

export interface SimParams {
  // World
  worldWidth: number;
  worldHeight: number;

  // Grass resource grid (Nature of Code Ch. 7 style local regrowth)
  grassCell: number; // pixel size of a grass cell
  grassRegrowth: number; // growth added per second, per cell (logistic)
  grassMax: number; // max energy in a cell, on the most fertile ground
  seasonAmplitude: number; // 0..1, how much season modulates regrowth
  seasonPeriod: number; // seconds per full seasonal cycle
  /**
   * Seconds per day/night cycle. Drives activity rhythm — animals feed at dawn
   * and dusk and bed down between (see Species.activityRhythm).
   *
   * COMPRESSED, and it has to be: life history runs on a clock where a year is
   * 90 s (scale.ts), which would make a literal day a quarter of a second. This
   * is set instead to a length a person can watch a herd move through.
   */
  dayPeriod: number;

  // Spatial structure. A procedural fertility field varies carrying capacity
  // across the map, so there are meadows worth fighting over and thin ground
  // that still carries a few survivors through a crash. Uniform ground makes
  // every local population rise and fall in lockstep, which is what drives the
  // whole system to ring itself to extinction.
  fertilityFloor: number; // fertility of the poorest ground, 0..1 (1 = uniform map)
  fertilityPatches: number; // coarse-octave lattice cells across the map; higher = finer patches

  // Initial populations. Seeded as social groups rather than scattered at
  // random — a founding population spread thinner than its own sense radius
  // can never find mates and dies out without breeding.
  initialHerbivores: number;
  initialCarnivores: number;
  herdSize: number; // deer per starting herd
  packSize: number; // wolves per starting pack

  /**
   * Life-history stretch — THE central trade in the model, and a per-run choice
   * rather than a hidden constant.
   *
   * 1  = fastest evolution. Lifespans are heavily compressed, so prey turn over
   *      far faster than they eat, prey production is inflated, and predators
   *      end up ~10x too abundant. Fine if you are here to watch selection.
   * 12 = honest ecology. Each animal eats a realistic amount over its life, so
   *      the predator:prey ratio lands near reality — at the cost of evolution
   *      taking proportionally longer to watch.
   *
   * Only DEMOGRAPHY scales. Movement stays literal at every setting, so a deer
   * sprints at a real 64 km/h either way; a slower world is not a world of
   * different animals.
   *
   * Restart-scoped: animals already alive carry their decoded lifespans, so
   * changing this mid-run would leave two incompatible generations.
   */
  lifeScale: number;

  // Genetics
  mutationRate: number; // per-gene probability of mutation
  mutationScale: number; // sd of Gaussian nudge, in gene-normalized units

  // Rescue / immigration — prevents terminal extinction so the sandbox persists.
  // A species below its floor is topped up (cloning a survivor when one exists,
  // fresh random genome only on true extinction). Set floors to 0 to disable and
  // watch a closed system run to its natural end.
  minHerbivores: number;
  minCarnivores: number;

  // Simulation
  dt: number; // fixed timestep in seconds
  statSampleEvery: number; // sample stats every N ticks
}

// Distances are metres and speeds m/s (see scale.ts): the default world is a
// 4.8 km × 3.6 km stretch of country, about 17.3 km².
//
// Population densities are NOT realistic and can't be. Real gray wolf density
// runs around one wolf per 25–100 km², so even this 17.3 km² map would honestly
// hold well under one wolf. These are set for a visible,
// watchable ecosystem instead; the trait ranges and life-history ratios are
// where the real biology lives.
export const DEFAULT_PARAMS: SimParams = {
  // 17.3 km². MEASURED, and the single most important number in this file.
  // Closed-system stability, same seeds, 50k ticks:
  //     1.9 km² (the old default)   CV 0.45-0.83
  //    17.3 km²                     CV 0.14-0.17
  // Room is what stops the two populations ringing: a crash in one valley stays
  // in that valley instead of taking the whole map with it. It also decides
  // whether realistic wolf behaviour is affordable — dispersal costs nothing
  // here and doubles volatility on the small map, because a dispersal run there
  // covers a large fraction of the whole world.
  worldWidth: 4800,
  worldHeight: 3600,

  grassCell: 20,
  // PROVISIONAL — see "Known issue" in CLAUDE.md. Raised from (0.6, 16) when the
  // mass decode was corrected (traits.ts): at true body size a deer burns ~2.3
  // energy/s at rest, but a 20 m cell held only ~9 energy and regrew at under
  // 1/s, so deer starved standing on a map that was 99% full.
  //
  // These values are the least-bad point found, NOT a solution. Measured closed
  // (floors off): at 40k ticks 3/4 seeds persist, but run to 80k and the cycle
  // rings itself out — wolves peak at 658, strip the herd from 2,601 to 2, and
  // die at tick 66k. Leaner grass does not help: 60 and 70 both starve the
  // wolves out by 22k because the herd never reaches the ~200 deer they need to
  // keep finding prey. There is no grass value that gives stable coexistence,
  // because nothing caps the wolves from above — one 400-1,360 energy carcass
  // funds up to eight pups at a gestation cost of 100-450. The fix is structural
  // (pack reproductive suppression), not another constant.
  //
  // With the floors ON (what actually ships) 3/3 seeds reach 80k, but read that
  // number carefully: on seed 1234 it does so with deer pinned at their rescue
  // floor of 10 and 139 wolves living off rescue-spawned prey. That inverts the
  // pre-fix failure rather than curing it — it used to be the wolves leaning on
  // the floor, and now it is the deer.
  grassRegrowth: 1.2,
  grassMax: 140,
  seasonAmplitude: 0.4,
  seasonPeriod: 240,
  dayPeriod: 40, // six "days" per season — fast enough to watch a herd bed down
  // and rise again, slow enough that a bout is a meaningful fraction of one

  fertilityFloor: 0.15,
  fertilityPatches: 5,

  initialHerbivores: 1620, // ~94 deer/km², matching the density the old map ran at
  initialCarnivores: 144,
  herdSize: 25, // white-tailed deer band in small family groups
  packSize: 8, // a typical gray wolf pack; the founding population seeds ~18 of
  // them across the default map

  lifeScale: 1, // see the startup choice; 1 = watch evolution, 12 = watch animals

  mutationRate: 0.08,
  mutationScale: 0.12,

  minHerbivores: 10,
  minCarnivores: 4,

  dt: 0.1,
  statSampleEvery: 5,
};

export const DEFAULT_SEED = 1234;
